import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import folderIcon from "../../assets/management/folder.svg";
import disabledFolderIcon from "../../assets/management/folder_disabled.svg";
import selectedFolderIcon from "../../assets/management/folder_selected.svg";
import patientsIcon from "../../assets/management/manage_patients.svg";
import disabledPatientsIcon from "../../assets/management/manage_patients_disabled.svg";
import selectedPatientsIcon from "../../assets/management/manage_patients_selected.svg";
import studyIcon from "../../assets/management/study.svg";
import disabledStudyIcon from "../../assets/management/study_disabled.svg";
import selectedStudyIcon from "../../assets/management/study_selected.svg";
import usersIcon from "../../assets/users/users.svg";
import disabledUsersIcon from "../../assets/users/users_disabled.svg";
import selectedUsersIcon from "../../assets/users/users_selected.svg";
import ManageEntities from "../../components/Management/ManageEntities/ManageEntities";
import ManagePatients from "../../components/Management/ManagePatients/ManagePatients";
import ManageProUsers from "../../components/Management/ManageProUsers/ManageProUsers";
import ManageStudies from "../../components/Management/ManageStudies/ManageStudies";
import StateIcon from "../../components/Shared/StateIcon/StateIcon";
import Tabs, { Tab } from "../../components/Shared/Tabs/Tabs";
import { useAuthContext } from "../../context/AuthContext";
import { ManageEntitiesContextProvider } from "../../context/ManageEntitiesContext";
import { ManageStudiesContextProvider } from "../../context/ManageStudiesContext";
import { ManageUsersContextProvider } from "../../context/ManageUsersContext";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../../hooks/useAuthorizedAction";
import useResponsive from "../../hooks/useResponsive";
import { Permissions } from "../../models/permission";
import Role from "../../models/role";
import authorizationService from "../../services/authorization.service";
import styles from "./ManagementPage.module.css";

enum TabKeys {
  ManageEntities = "manage_entities",
  ManageUsers = "manage_users",
  ManageStudies = "manage_studies",
  ManagePatients = "manage_patients",
}

const ManagementPage = () => {
  const {
    state: { user },
  } = useAuthContext();

  const isCroOrg = useAuthorizedAction(AuthorizedActionsKeys.ViewAsCroOrg);
  const isCroExternOrg = useAuthorizedAction(
    AuthorizedActionsKeys.ViewAsCroExternOrg
  );
  const [selectedTab, setSelectedTab] = useState<TabKeys>();

  useEffect(() => {
    if (!tabs) return;

    // Init as default tab (first tab)
    let selectedTab = tabs[0].id as TabKeys;

    if (isCroOrg || isCroExternOrg) {
      if (isAdmin) {
        selectedTab = TabKeys.ManageStudies;
      } else if (canManageNonProUsers) {
        selectedTab = TabKeys.ManagePatients;
      }
    }

    setSelectedTab(selectedTab);
  }, []); // eslint-disable-line

  const { isTabletPortrait } = useResponsive();

  const isAdmin =
    user && authorizationService.hasRole(user.role, [Role.OrgAdmin]);

  const canManageNonProUsers =
    user &&
    authorizationService.hasPermissions(user.permissions, [
      Permissions.CanManageNonProUsers,
    ]);

  const canManageProUsers =
    user &&
    authorizationService.hasPermissions(user.permissions, [
      Permissions.CanManageProUsers,
    ]);

  const onTabChangeHandler = (id: string | null) => {
    if (!id) return;
    setSelectedTab(id as TabKeys);
  };

  const showEditPanel =
    selectedTab === TabKeys.ManageUsers ||
    selectedTab === TabKeys.ManagePatients;

  let tabs: Tab[] = [];

  if ((isCroOrg || isCroExternOrg) && isAdmin) {
    tabs.push(
      // Manage Studies
      {
        id: TabKeys.ManageStudies,
        title: (
          <TabTitle
            selected={selectedTab === TabKeys.ManageStudies}
            tab={TabKeys.ManageStudies}
          />
        ),
        body: (
          <ManageStudiesContextProvider>
            <ManageStudies />
          </ManageStudiesContextProvider>
        ),
      }
    );
  }

  if (isAdmin) {
    tabs.push({
      // Manage Entities
      id: TabKeys.ManageEntities,
      title: (
        <TabTitle
          selected={selectedTab === TabKeys.ManageEntities}
          tab={TabKeys.ManageEntities}
        />
      ),
      body: (
        <ManageEntitiesContextProvider orgId={user!.organization!.id}>
          <ManageEntities />
        </ManageEntitiesContextProvider>
      ),
    });
  }

  if (canManageProUsers) {
    tabs.push(
      // Manage Users
      {
        id: TabKeys.ManageUsers,
        title: (
          <TabTitle
            selected={selectedTab === TabKeys.ManageUsers}
            tab={TabKeys.ManageUsers}
          />
        ),
        body: (
          <ManageUsersContextProvider>
            <ManageProUsers />
          </ManageUsersContextProvider>
        ),
        bodyClassName: "h-100",
      }
    );
  }

  if (canManageNonProUsers) {
    tabs.push(
      // Manage Patients
      {
        id: TabKeys.ManagePatients,
        title: (
          <TabTitle
            selected={selectedTab === TabKeys.ManagePatients}
            tab={TabKeys.ManagePatients}
          />
        ),
        body: (
          <ManageUsersContextProvider>
            <ManagePatients />
          </ManageUsersContextProvider>
        ),
        bodyClassName: "h-100",
      }
    );
  }

  return (
    <div className="h-100 w-100">
      <div className={`d-flex ${styles.minHeight100}`}>
        <div
          className={`${
            showEditPanel
              ? isTabletPortrait
                ? styles.tabletManageUsers
                : "w-75"
              : "w-100"
          }`}
        >
          <Tabs
            defaultActiveKey={tabs[0].id as TabKeys}
            activeKey={selectedTab}
            tabs={tabs}
            onSelect={onTabChangeHandler}
            navBarClassName="pl-3 pt-3"
            contentClassName="overflow-auto flex-grow-1"
            unmountOnExit={true}
          />
        </div>
        {showEditPanel && (
          <>
            <div className={styles.userDetailsDivider}></div>
            <div
              className={`${
                isTabletPortrait ? styles.tabletEditPanel : "w-25"
              }`}
            >
              {/* user extra info will be rendered here as React Portal*/}
              <div id="userInfoWrapper" className="h-100"></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ManagementPage;

const getTabIcons = (
  tab: TabKeys
): {
  icon: string;
  selectedIcon: string;
  disabledIcon: string;
  className?: string;
} => {
  let icon = "";
  let selectedIcon = "";
  let disabledIcon = "";
  let className;

  switch (tab) {
    case TabKeys.ManageEntities:
      icon = folderIcon;
      selectedIcon = selectedFolderIcon;
      disabledIcon = disabledFolderIcon;
      break;
    case TabKeys.ManageUsers:
      icon = usersIcon;
      selectedIcon = selectedUsersIcon;
      disabledIcon = disabledUsersIcon;
      break;
    case TabKeys.ManagePatients:
      icon = patientsIcon;
      selectedIcon = selectedPatientsIcon;
      disabledIcon = disabledPatientsIcon;
      break;
    case TabKeys.ManageStudies:
      icon = studyIcon;
      selectedIcon = selectedStudyIcon;
      disabledIcon = disabledStudyIcon;
      className = styles.studyIcon;
      break;
    default:
      throw new Error(`Invalid tab type ${tab}`);
  }
  return {
    icon,
    selectedIcon,
    disabledIcon,
    className,
  };
};

const getTitleText = (tab: TabKeys) => {
  switch (tab) {
    case TabKeys.ManageEntities:
      return (
        <FormattedMessage
          id="MANAGEMENT.MANAGE_ENTITIES"
          defaultMessage="Manage Entities"
        />
      );
    case TabKeys.ManageUsers:
      return (
        <FormattedMessage
          id="MANAGEMENT.MANAGE_PRO_USERS"
          defaultMessage="Manage Professional Users"
        />
      );
    case TabKeys.ManagePatients:
      return (
        <FormattedMessage
          id="MANAGEMENT.MANAGE_PATIENTS"
          defaultMessage="Manage Patients Accounts"
        />
      );
    case TabKeys.ManageStudies:
      return (
        <FormattedMessage
          id="MANAGEMENT.MANAGE_STUDIES"
          defaultMessage="Manage Studies"
        />
      );
    default:
      throw new Error(`Invalid tab type ${tab}`);
  }
};

const TabTitle = ({ tab, selected }: { tab: TabKeys; selected: boolean }) => {
  const { icon, selectedIcon, disabledIcon, className } = getTabIcons(tab);
  const titleText = getTitleText(tab);
  return (
    <div className="d-flex align-items-center">
      <StateIcon
        className={`${styles.tabsIcon} ${className ?? ""}`}
        iconSrc={icon}
        selected={{
          iconSrc: selectedIcon,
          value: selected,
        }}
        disabled={{
          iconSrc: disabledIcon,
          value: false,
        }}
      />
      <span className="mx-sm"></span>
      {titleText}
    </div>
  );
};
